/**
 * @file ShopContractRow.tsx
 * @description Строка готовности «Договор с платформой» во вкладке «Обзор»
 * (phase19.28 этап А; PL9, PL18: договор (оферта) Commora и поручение на
 * обработку ПДн обязательны до первого открытия сайта; галочкой не
 * снимается). «Отметить» — окно «дата, номер»; «Снять отметку» — с
 * подтверждением. `PATCH /admin/platform/shops/:shopId/contract`,
 * право `PLATFORM_MANAGE_SHOPS`; после ответа карточка перечитывается.
 */

import { CircleCheck, OctagonAlert } from "lucide-react";
import { useState } from "react";

import { platformShops, type ShopCard } from "../../../lib/api-platform-shops";
import { apiErrorText } from "../../../lib/platform/shop-errors";
import { formatPlatformDate } from "../../../lib/platform/shop-format";
import { Button } from "../../ui/button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { useNotify } from "../../ui/NotifyProvider";
import { useConfirm } from "../../ui/use-confirm";
import { ShopContractDialog } from "./ShopContractDialog";

/** Кто отметил: имя, иначе почта. */
function markedByLabel(markedBy: NonNullable<ShopCard["platformContract"]>["markedBy"]): string | null {
  return markedBy ? markedBy.displayName || markedBy.email : null;
}

interface Props {
  shop: ShopCard;
  onChanged: () => void;
}

export function ShopContractRow({ shop, onChanged }: Props) {
  const notify = useNotify();
  const confirmApi = useConfirm();
  const [marking, setMarking] = useState(false);
  const contract = shop.platformContract ?? null;

  async function unmark() {
    const ok = await confirmApi.confirm({
      title: "Снять отметку о договоре?",
      description: "Без договора с платформой магазин не сможет открыть сайт. Уже открытый сайт отметка не закрывает.",
      action: "Снять отметку",
      danger: true,
    });
    if (!ok) return;
    try {
      await platformShops.setContract(shop.id, { signedAt: null, number: null });
      notify.success("Отметка о договоре снята");
      onChanged();
    } catch (err) {
      notify.error(apiErrorText(err, "Не удалось снять отметку"));
    }
  }

  const who = contract ? markedByLabel(contract.markedBy) : null;
  return (
    <div className="flex flex-col gap-2 border-b border-[var(--border)] pb-3 sm:flex-row sm:items-center">
      <span className="flex min-w-0 flex-1 items-start gap-3">
        {contract ? (
          <CircleCheck aria-hidden className="mt-0.5 size-5 shrink-0 text-[var(--status-success-fg,var(--success))]" />
        ) : (
          <OctagonAlert aria-hidden className="mt-0.5 size-5 shrink-0 text-[var(--status-error-fg,var(--destructive))]" />
        )}
        <span className="flex min-w-0 flex-col">
          <span className="type-body font-medium">Договор с платформой</span>
          <span className="type-caption break-words text-on-surface-variant">
            {contract
              ? [`заключён ${formatPlatformDate(contract.signedAt)}`, contract.number ? `№ ${contract.number}` : null, who ? `отметил ${who}` : null]
                  .filter(Boolean)
                  .join(" · ")
              : "не отмечен — без договора сайт открыть нельзя"}
          </span>
        </span>
      </span>
      {contract ? (
        <Button variant="outline" className="self-start sm:self-auto" onClick={() => void unmark()}>
          Снять отметку
        </Button>
      ) : (
        <Button variant="soft" className="self-start sm:self-auto" onClick={() => setMarking(true)}>
          Отметить
        </Button>
      )}
      {marking ? (
        <ShopContractDialog
          shopId={shop.id}
          onClose={() => setMarking(false)}
          onSaved={() => {
            setMarking(false);
            onChanged();
          }}
        />
      ) : null}
      <ConfirmDialog request={confirmApi.request} onSettle={confirmApi.settle} onDismiss={confirmApi.dismiss} />
    </div>
  );
}
